"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter, usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  ONBOARDING_QUERY_PARAM,
  ONBOARDING_QUERY_VALUE,
} from "@/lib/onboarding-constants";
import {
  Bell,
  BellOff,
  BellPlus,
  Check,
  ChevronRight,
  ExternalLink,
  GitFork,
  Globe,
  Loader2,
  Mail,
  Pencil,
  Search,
  UserRound,
  Trash2,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { API_BASE_URL } from "@/lib/config";
import { createChannelLogger } from "@/lib/debug-log";
import { telemetry } from "@/lib/telemetry";
import {
  SLATE,
  DESTRUCTIVE,
  TONES,
  alertTone,
  type AlertTone,
  type ToneTokens,
  TITLE_CLASS,
  SUB_CLASS,
  BODY_CLASS,
  MONO_CLASS,
  PAD,
  RADIUS,
  SPRING_IN,
  SUCCESS_BURST,
} from "@/lib/alert-theme";
import { getViewModeChrome } from "../view-mode-chrome";

// Dedicated channel for the main-chat → alert-page edit_alert handoff flow.
// Writes to frontend_edit_alert_handoff.txt. This is hop #1 of the chain
// (the card that calls window.open); hops #2–#8 live on the alerts page
// and in chat.tsx.
const handoffLog = createChannelLogger("edit_alert_handoff");

interface AlertToolDisplayProps {
  status: "loading" | "success";
  result?: any;
  args?: any;
  sessionId?: string;
  toolCallId?: string;
  // Replay / read-only chats can't hit the live `/agent/tool-confirm`
  // endpoint — there's no agent loop listening on that session. When
  // this is true, the confirmation click instead releases the backend
  // replay stream's publish-gate pause via `/replay/control`.
  isReadonly?: boolean;
  viewMode?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Colocated primitives — icy/silver-blue language, Fork C.
// Never hardcode hex; always consume ToneTokens from alert-theme.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shared focus ring for every interactive element in this file.
 * Mode-aware: sky for alert, emerald for signal, slate otherwise.
 */
function focusRing(viewMode?: string) {
  const chrome = getViewModeChrome(viewMode ?? "alert");
  return `focus-visible:outline-none focus-visible:ring-2 ${chrome.focusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-background`;
}

const PUBLISH_EXPECTATION_COPY =
  "This usually takes 2-5 minutes. We run your alert once to make sure it works before turning it on.";

/**
 * Card shell — one cohesive surface, sharper corners (rounded-lg, not xl).
 *
 * `pad`:
 *  - "default" → result cards (px-[13px] py-[10px])
 *  - "confirm" → confirm gates (px-5 py-4)
 *  - "dense"   → compact list chips (px-3 py-2)
 */
function AlertCardShell({
  t,
  pad = "default",
  children,
  className = "",
}: {
  t: ToneTokens;
  pad?: keyof typeof PAD;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm ${className}`}
    >
      <div className={`flex flex-col ${PAD[pad]} gap-3`}>{children}</div>
    </div>
  );
}

/**
 * Layout A — inline single-row header. Preferred for result cards.
 *   [icon 7×7] [title · subtitle/chip] [right]
 */
function InlineHeader({
  t,
  Icon,
  title,
  right,
  children,
}: {
  t: ToneTokens;
  Icon: LucideIcon;
  title: React.ReactNode;
  right?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={`w-7 h-7 ${RADIUS.chip} ${t.iconBg} flex items-center justify-center shrink-0`}
      >
        <Icon className={`w-3.5 h-3.5 ${t.iconFg}`} strokeWidth={2} />
      </div>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`${TITLE_CLASS} truncate`}>{title}</span>
        {children}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

/**
 * Layout B — baseline-aligned header for confirm cards.
 *  [icon 9×9] [title]
 *              [subtitle]
 *  ↓ all subsequent block-level content reflows at full card width
 */
function BaselineHeader({
  t,
  Icon,
  title,
  subtitle,
}: {
  t: ToneTokens;
  Icon: LucideIcon;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={`w-9 h-9 ${RADIUS.chip} ${t.iconBg} flex items-center justify-center shrink-0`}
      >
        <Icon className={`w-4 h-4 ${t.iconFg}`} strokeWidth={2} />
      </div>
      <div className="flex flex-col min-w-0 flex-1 gap-0.5">
        <span className={TITLE_CLASS}>{title}</span>
        {subtitle && <span className={SUB_CLASS}>{subtitle}</span>}
      </div>
    </div>
  );
}

function MetaItem({
  Icon,
  t,
  children,
}: {
  Icon: LucideIcon;
  t: ToneTokens;
  children: React.ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${t.subLight} min-w-0`}>
      <Icon className={`w-3 h-3 ${t.iconFg} shrink-0`} />
      <span className="truncate tabular-nums">{children}</span>
    </span>
  );
}

function MetaRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      {children}
    </div>
  );
}

function StatusChip({
  t,
  label,
}: {
  t: ToneTokens;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${RADIUS.pill} border ${t.chipBg} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${t.chipText} tabular-nums`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${t.chipDot}`} />
      {label}
    </span>
  );
}

/**
 * Right-aligned action row. Small h-8 buttons with gap-2, NOT full-width split.
 */
function ActionRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-2 pt-1">{children}</div>;
}

/**
 * Icy solid primary button (publish).
 */
function PrimaryButton({
  onClick,
  disabled,
  busy,
  Icon,
  children,
  variant = "icy",
  viewMode,
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  Icon: LucideIcon;
  children: React.ReactNode;
  /** `icy` for publish; `pearl` for delete (both readable on slate-950). */
  variant?: "icy" | "pearl";
  viewMode?: string;
}) {
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const base =
    variant === "icy"
      ? chrome.button
      : "bg-slate-100 hover:bg-white text-slate-950";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 ${RADIUS.button} ${base} disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors ${focusRing(viewMode)}`}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      )}
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick,
  disabled,
  busy,
  Icon,
  children,
  viewMode,
}: {
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  Icon: LucideIcon;
  children: React.ReactNode;
  viewMode?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-slate-100 transition-colors ${focusRing(viewMode)}`}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" strokeWidth={2.4} />
      )}
      {children}
    </button>
  );
}

/**
 * Inline loader used for pre-preview + transient submit states.
 */
function InlineLoader({
  tone,
  label,
}: {
  tone: AlertTone;
  label: string;
}) {
  const t = TONES[tone];
  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden`}>
        <div className={`${PAD.default} flex items-center gap-3`}>
          <div
            className={`w-7 h-7 ${RADIUS.chip} ${t.iconBg} flex items-center justify-center shrink-0`}
          >
            <Loader2 className={`w-3.5 h-3.5 ${t.iconFg} animate-spin`} strokeWidth={2} />
          </div>
          <span className={`text-sm ${t.subLight} leading-tight`}>{label}</span>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Multi-stage loader for long-running flows (e.g. upload_alert publish).
 * Cycles through labels on timers so the user sees progress, even though
 * we don't have real per-stage events from the backend yet.
 *
 * Stages are { label, minMs } — the loader advances past each stage after
 * `minMs` elapses, and stays on the last stage until the tool result arrives
 * and replaces this component.
 */
function MultiStageLoader({
  tone,
  stages,
  note,
  rightSlot,
}: {
  tone: AlertTone;
  stages: { label: string; minMs: number }[];
  note?: string;
  /** Optional trailing control rendered inside the header row on the right.
   *  Used here for the inline cancel-upload X so the cancel sits on the
   *  same row as the stage label instead of taking its own row. */
  rightSlot?: React.ReactNode;
}) {
  const t = TONES[tone];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index >= stages.length - 1) return;
    const current = stages[index];
    const timeout = window.setTimeout(() => {
      setIndex((i) => Math.min(i + 1, stages.length - 1));
    }, current.minMs);
    return () => window.clearTimeout(timeout);
  }, [index, stages]);

  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden`}>
        <div className={`${PAD.default} flex items-center gap-3`}>
          <div
            className={`w-7 h-7 ${RADIUS.chip} ${t.iconBg} flex items-center justify-center shrink-0`}
          >
            <Loader2 className={`w-3.5 h-3.5 ${t.iconFg} animate-spin`} strokeWidth={2} />
          </div>
          <div className="flex flex-col min-w-0 flex-1 gap-1">
            <motion.span
              key={index}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`text-sm ${t.subLight} leading-tight truncate`}
            >
              {stages[index]?.label}
            </motion.span>
            <div
              className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${t.subLight} opacity-60`}
            >
              <span>
                Step {index + 1} of {stages.length}
              </span>
              <div className="flex items-center gap-1 ml-1">
                {stages.map((_, i) => (
                  <span
                    key={i}
                    className={`h-0.5 w-4 rounded-full ${
                      i <= index ? t.chipDot : "bg-slate-500/30"
                    }`}
                  />
                ))}
              </div>
            </div>
            {note && (
              <span className={`text-[11px] ${t.subLight} opacity-75 leading-snug`}>
                {note}
              </span>
            )}
          </div>
          {rightSlot ? <div className="shrink-0 ml-1">{rightSlot}</div> : null}
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared confirmation hook — polls preview and manages submit state.
// ─────────────────────────────────────────────────────────────────────────────

type ConfirmDecision = "pending" | "confirming" | "cancelling" | "done";

// Persist the user's decision per tool_call_id so a remount (e.g. switching
// between chat column and artifact column) or a second copy of this card
// rendering elsewhere both see the same state and don't flip back to the
// X/✓ buttons after the user already clicked.
function readPersistedDecision(confirmationId: string): ConfirmDecision {
  if (!confirmationId || typeof window === "undefined") return "pending";
  try {
    const raw = window.sessionStorage.getItem(`tool_confirm:${confirmationId}`);
    if (raw === "confirming" || raw === "cancelling" || raw === "done") {
      return raw;
    }
  } catch {
    // sessionStorage unavailable — fall back to pending
  }
  return "pending";
}

function writePersistedDecision(confirmationId: string, decision: ConfirmDecision) {
  if (!confirmationId || typeof window === "undefined") return;
  try {
    if (decision === "pending") {
      window.sessionStorage.removeItem(`tool_confirm:${confirmationId}`);
    } else {
      window.sessionStorage.setItem(`tool_confirm:${confirmationId}`, decision);
    }
  } catch {
    // sessionStorage unavailable — best-effort only
  }
}

function useToolConfirmation(
  sessionId: string | undefined,
  toolCallId: string | undefined,
  enabled: boolean,
  // When true, the card belongs to a replay / read-only chat and the
  // submit+poll endpoints must be re-routed. Polling is skipped (the
  // preview endpoint 404s for replay sessions and spams the console),
  // and the confirm click releases the replay stream's publish-gate
  // via /replay/control instead of hitting /agent/tool-confirm.
  replayMode: boolean = false,
) {
  const [preview, setPreview] = useState<any | null>(null);
  const confirmationId = toolCallId || "";
  // REPLAY NOTE: In replay mode we intentionally skip sessionStorage.
  // The tool_call_id is baked into the recording (same across every page
  // load), so persisting "done" would wedge the card in the post-confirm
  // loader on the NEXT visit — the Publish button never renders, and the
  // backend publish-gate auto-times out (30s) without a real click. Live
  // chats still persist across remounts (chat col ↔ artifact col) as
  // before; replay instances start fresh on every page load.
  const [decision, setDecision] = useState<ConfirmDecision>(() =>
    replayMode ? "pending" : readPersistedDecision(confirmationId),
  );
  const [error, setError] = useState<string | null>(null);

  // Re-sync decision from sessionStorage whenever the card remounts under a
  // known confirmationId (e.g. artifact column mounting after a chat-column
  // click already happened). Replay mode intentionally bypasses this —
  // see note above.
  useEffect(() => {
    if (!confirmationId || replayMode) return;
    const persisted = readPersistedDecision(confirmationId);
    if (persisted !== "pending") setDecision(persisted);
  }, [confirmationId, replayMode]);

  useEffect(() => {
    if (!enabled || !confirmationId) return;
    // In replay mode we have everything we need from `args` (the
    // streamed tool-call arguments carry name/description/schedule).
    // The live preview endpoint only exists for real in-flight agents.
    if (replayMode) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await apiClient.getToolConfirmPreview(confirmationId);
        if (!cancelled && res?.preview) {
          setPreview(res.preview);
          return;
        }
      } catch {
        // ignore — keep trying
      }
      if (!cancelled && attempts < 30) {
        setTimeout(poll, 1000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [confirmationId, enabled, replayMode]);

  const submit = async (choice: "confirm" | "cancel") => {
    if (!sessionId || !confirmationId) return;
    setError(null);
    const inFlight: ConfirmDecision = choice === "confirm" ? "confirming" : "cancelling";
    setDecision(inFlight);
    if (!replayMode) writePersistedDecision(confirmationId, inFlight);
    try {
      if (replayMode) {
        // Route the click to /replay/control. Confirm → resume the
        // backend's publish-gate busy-wait (the tool result then
        // streams in and the card transitions to its success state).
        // Cancel → skip to end of the current turn so the viewer
        // isn't stuck at the gate; this mirrors the skip button in
        // <ReplayControls>.
        const action = choice === "confirm" ? "resume" : "skip";
        const res = await fetch(`${API_BASE_URL}/replay/control`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, action }),
        });
        if (!res.ok) {
          throw new Error(`replay/control ${res.status}`);
        }
        // Tell chat.tsx to clear its bottom-bar Publish button (same
        // publish_gate event sourced both UIs; we just resolved it).
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("xynth:replay-publish-confirmed", {
              detail: { sessionId, toolCallId: confirmationId, action },
            }),
          );
        }
      } else {
        await apiClient.submitToolConfirm(sessionId, confirmationId, choice);
      }
      setDecision("done");
      if (!replayMode) writePersistedDecision(confirmationId, "done");
    } catch (err: any) {
      console.error("[ToolConfirm] submit failed", err);
      setError(
        err?.message?.includes("401")
          ? "Not signed in. Please refresh and sign in again."
          : err?.message || "Couldn't send your response. Try again.",
      );
      setDecision("pending");
      if (!replayMode) writePersistedDecision(confirmationId, "pending");
    }
  };

  return { preview, decision, error, submit };
}

// ─────────────────────────────────────────────────────────────────────────────
// PublishConfirmModal — blocking modal for the first publish click.
// Sits on top of the existing inline card so the user can't miss the decision.
// Same submit handler as the inline card → clicking either resolves the
// backend wait. Modal auto-dismisses once decision moves out of "pending".
// Skipped entirely on replay/readonly (existing inline behavior is fine
// there) and on auto-approved retries (the inline card never even renders).
// ─────────────────────────────────────────────────────────────────────────────

function PublishConfirmModal({
  open,
  name,
  description,
  scheduleDescription,
  email,
  warning,
  renameFrom,
  submitting,
  errorMessage,
  onConfirm,
  onCancel,
  viewMode,
}: {
  open: boolean;
  name: string;
  description: string;
  scheduleDescription: string;
  email: string;
  warning?: string;
  renameFrom?: string | null;
  submitting: boolean;
  errorMessage: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  viewMode?: string;
}) {
  const t = alertTone(viewMode);
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
      if (e.key === "Enter" && !submitting) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel, onConfirm]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) onCancel();
          }}
          data-testid="upload-alert-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-alert-modal-title"
            className={`w-full max-w-md ${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-2xl`}
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2.5">
              <BellPlus className={`w-5 h-5 ${t.iconFg} shrink-0`} strokeWidth={2.2} />
              <h2 id="publish-alert-modal-title" className="text-base font-semibold text-white/95 truncate">
                Publish "{name || "Untitled alert"}"?
              </h2>
            </div>

            <div className="px-5 py-4 space-y-3 text-[13px]">
              {renameFrom && (
                <div className="flex items-start gap-2 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-400/25 rounded-md px-3 py-2">
                  <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" />
                  <span>
                    This will rename and replace your existing alert "{renameFrom}".
                    Want to keep "{renameFrom}" as is? Cancel, then create a new alert
                    from the Alerts page instead.
                  </span>
                </div>
              )}
              {description && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1">What it does</div>
                  <div className="text-white/85 leading-snug">{description}</div>
                </div>
              )}
              {scheduleDescription && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1">Schedule</div>
                  <div className="text-white/85">{scheduleDescription}</div>
                </div>
              )}
              {email && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1">Notifications to</div>
                  <div className="text-white/85 truncate">{email}</div>
                </div>
              )}
              <div className="text-[11px] text-white/55 leading-snug pt-1 border-t border-white/5">
                {PUBLISH_EXPECTATION_COPY}
              </div>
              {warning && (
                <div className={`flex items-start gap-2 text-xs ${t.subLight} bg-white/[0.03] border border-white/10 rounded-md px-3 py-2`}>
                  <TriangleAlert className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${t.iconFg}`} />
                  <span>{warning}</span>
                </div>
              )}
              {errorMessage && (
                <div className="text-xs text-red-300/90 bg-red-500/5 border border-red-400/20 rounded-md px-3 py-2">
                  {errorMessage}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 bg-black/20">
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className={`px-3 py-1.5 text-sm rounded-md border border-white/15 text-white/80 hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${focusRing(viewMode)}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={submitting}
                data-testid="upload-alert-modal-publish"
                className={`px-4 py-1.5 text-sm font-medium rounded-md ${chrome.button} transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 ${focusRing(viewMode)}`}
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.4} /> : <Check className="w-3.5 h-3.5" strokeWidth={2.4} />}
                {submitting ? "Publishing…" : "Publish"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UploadAlertDisplay — publish confirm / success / first-run-failed / cancelled
// ─────────────────────────────────────────────────────────────────────────────

export function UploadAlertDisplay({
  status,
  result,
  args,
  sessionId,
  toolCallId,
  isReadonly,
  viewMode,
}: AlertToolDisplayProps) {
  // Router is used below to turn the "View alert" link into a SPA
  // navigation (same tab, no full reload). Dropping target="_blank"
  // alone would fall through to a full browser navigation on an
  // external-origin href; going through useRouter().push() lets
  // Next.js handle it as a client-side transition to /alerts/[id].
  const router = useRouter();
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const t = alertTone(viewMode);
  const loaderTone: AlertTone = viewMode === "signal" ? "emerald" : "icy";
  // Clean replay route (`/replay/<id>/clean`) deliberately strips the
  // onboarding handoff: the clean tour has no buddy overlays, so
  // landing on the alert page with `?onboarding=1` would re-trigger
  // the alert-page buddy tour and break the no-overlay contract. We
  // detect the clean route by suffix match on the current pathname.
  const pathname = usePathname();
  const isCleanReplay = Boolean(pathname && pathname.endsWith("/clean"));
  const confirmation = useToolConfirmation(
    sessionId,
    toolCallId,
    status === "loading",
    // isReadonly is true for replays and shared snapshots. Both cases
    // have no live agent to confirm against; the hook reroutes the
    // click to /replay/control (or is a silent no-op for snapshots).
    Boolean(isReadonly),
  );

  // Mid-upload cancel (post-Publish, pre-result). Separate from the pre-
  // Publish `confirmation.submit("cancel")` path — that one writes to
  // `tool_confirm:*` and only works BEFORE the user confirms. Once the
  // user clicks Publish, `_upload_alert` is running through its stages
  // (fork → alert doc → linked session → first-run → scheduler). This
  // state tracks the new `/agent/upload-alert-cancel` endpoint, which
  // writes `alert_upload_cancel:{tool_call_id}` in Redis. The backend's
  // checkpoints see it and run `delete_alert_cascade` to roll back.
  const [midCancel, setMidCancel] = useState<"idle" | "cancelling">("idle");
  const [midCancelError, setMidCancelError] = useState<string | null>(null);
  const requestMidUploadCancel = async () => {
    if (!sessionId || !toolCallId || midCancel === "cancelling") return;
    setMidCancelError(null);
    setMidCancel("cancelling");
    try {
      await apiClient.cancelUploadAlert(sessionId, toolCallId);
      // Don't flip back to idle — we wait for the backend tool result to
      // arrive with status==="cancelled", at which point the outer
      // `status` prop flips to "success" and the result-state branch
      // renders the final cancelled card.
    } catch (err: any) {
      setMidCancel("idle");
      setMidCancelError(
        err?.message?.includes("401")
          ? "Not signed in. Please refresh and sign in again."
          : err?.message || "Couldn't send the cancel. Try again.",
      );
    }
  };

  if (status === "loading") {
    const preview = confirmation.preview || args || {};
    const name = preview.name || args?.name || "";
    const description = preview.description || args?.description || "";
    const scheduleDescription =
      preview.schedule_description || args?.schedule_description || preview.schedule || "";
    const email = preview.notification_email || args?.notification_email || "";
    const warning = preview.warning;
    const renameFrom =
      typeof preview.rename_from === "string" && preview.rename_from.trim()
        ? preview.rename_from
        : null;
    const ready = Boolean(confirmation.preview || (args?.name && args?.description));

    if (confirmation.decision === "done" || confirmation.decision === "confirming") {
      const isCancelling = midCancel === "cancelling";
      // Inline cancel: a single circular icon-button sitting in the loader's
      // header row (right side), replacing the full-width "× Cancel upload"
      // row below. User-requested layout: "just a cancel circle cancel icon
      // in the same row, on the right". The surrounding w-7 h-7 rounded-full
      // hit-area matches the leading spinner chip so the loader stays visually
      // balanced. Triggers the same /agent/upload-alert-cancel endpoint as
      // before; all cancel-cascade semantics preserved.
      const cancelSlot = (
        <button
          type="button"
          onClick={requestMidUploadCancel}
          disabled={isCancelling}
          aria-label={isCancelling ? "Cancelling upload…" : "Cancel upload"}
          title={isCancelling ? "Cancelling…" : "Cancel upload"}
          className={`inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-400/30 bg-transparent text-slate-200 hover:bg-slate-100/5 hover:border-slate-300/60 hover:text-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${focusRing(viewMode)}`}
        >
          {isCancelling ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.4} />
          ) : (
            <X className="w-3.5 h-3.5" strokeWidth={2.4} />
          )}
        </button>
      );
      return (
        <motion.div {...SPRING_IN} className="w-full">
          <div className="flex flex-col gap-1.5">
            <MultiStageLoader
              tone={loaderTone}
              stages={
                isReadonly
                  ? [
                      // Replay: each stage exactly 3s for a snappy demo
                      // cadence. Backend's cinematic delay is matched to
                      // ~13s so all 4 stages play through and the user
                      // gets a brief beat on "Finalizing…" before the
                      // success card replaces the loader.
                      { label: "Saving your alert…", minMs: 3000 },
                      { label: "Running a quick test…", minMs: 3000 },
                      { label: "Turning your alert on…", minMs: 3000 },
                      { label: "Finishing up…", minMs: 3000 },
                    ]
                  : [
                      // Live: timings reflect real backend work.
                      { label: "Saving your alert…", minMs: 4000 },
                      { label: "Running a quick test…", minMs: 16000 },
                      { label: "Turning your alert on…", minMs: 8000 },
                      { label: "Finishing up…", minMs: 60000 },
                    ]
              }
              // Only show the live "2-5 min" expectation in real
              // publishes. During replay/readonly the loader finishes
              // in ~13s, so the copy would be misleading.
              note={isReadonly ? undefined : PUBLISH_EXPECTATION_COPY}
              rightSlot={cancelSlot}
            />
            {midCancelError && (
              <div className="flex justify-end px-1">
                <span className="text-[11px] text-red-300/90">{midCancelError}</span>
              </div>
            )}
          </div>
        </motion.div>
      );
    }
    if (confirmation.decision === "cancelling") {
      return <InlineLoader tone="slate" label="Cancelling…" />;
    }

    if (!ready) {
      return <InlineLoader tone={loaderTone} label="Preparing alert preview…" />;
    }

    // Show the blocking modal alongside the inline card so users can't miss
    // the decision. Skip on replay/readonly — those paths use /replay/control
    // and the existing inline card is already wired correctly there.
    const modalOpen = !isReadonly && ready && confirmation.decision === "pending";
    return (
      <motion.div
        {...SPRING_IN}
        className="w-full"
        // Buddy-tour anchor for the publish-gate beat. The replay
        // stream pauses on `publish_gate` and emits a buddy beat that
        // spotlights this card and points the click-target ring at
        // the Publish button below.
        data-testid="upload-alert-confirm"
      >
        <PublishConfirmModal
          open={modalOpen}
          name={name}
          description={description}
          scheduleDescription={scheduleDescription}
          email={email}
          warning={warning}
          renameFrom={renameFrom}
          submitting={false}
          errorMessage={confirmation.error}
          onConfirm={() => confirmation.submit("confirm")}
          onCancel={() => confirmation.submit("cancel")}
          viewMode={viewMode}
        />
        <div className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm`}>
          <div className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-center">
            <div className="flex items-center gap-2 min-w-0">
              <BellPlus className={`w-4 h-4 ${t.iconFg} shrink-0`} strokeWidth={2.2} />
              <span className={`${TITLE_CLASS} truncate`}>
                Publish {name || "Untitled alert"}?
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                aria-label="Cancel"
                title="Cancel"
                onClick={() => confirmation.submit("cancel")}
                className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 text-slate-100 transition-colors ${focusRing(viewMode)}`}
              >
                <X className="w-4 h-4" strokeWidth={2.4} />
              </button>
              <button
                type="button"
                aria-label="Publish"
                title="Publish"
                onClick={() => confirmation.submit("confirm")}
                // Buddy click_target anchor — the green pulsing ring
                // and "Click" pill land on this exact button.
                data-testid="upload-alert-publish-button"
                className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} ${chrome.button} transition-colors ${focusRing(viewMode)}`}
              >
                <Check className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>
          </div>
          <div className={`border-t ${t.ring} px-3 py-2 text-[11px] ${t.subLight} leading-snug`}>
            {PUBLISH_EXPECTATION_COPY}
          </div>
          {renameFrom && (
            <div className={`border-t ${t.ring} px-3 py-2 flex items-start gap-2 text-xs text-amber-200/90`}>
              <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" />
              <span>
                This will rename and replace "{renameFrom}". To keep it, cancel and
                create a new alert from the Alerts page instead.
              </span>
            </div>
          )}
          {warning && (
            <div className={`border-t ${t.ring} px-3 py-2 flex items-start gap-2 text-xs ${t.subLight}`}>
              <TriangleAlert className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${t.iconFg}`} />
              <span>{warning}</span>
            </div>
          )}
          {confirmation.error && (
            <div className={`border-t ${t.ring} px-3 py-2 text-xs text-red-300/90`}>{confirmation.error}</div>
          )}
        </div>
      </motion.div>
    );
  }

  // ── Result states ──────────────────────────────────────────────────
  const alertName = result?.alert_name || result?.name || args?.name || "Alert";
  const alertId = result?.alert_id || "";
  const scheduleDescription = result?.schedule_description || result?.schedule || "";
  const description = result?.description || "";
  const email = result?.notification_email || "";
  // Prefer the deep link the backend returned. Fall back to building one from
  // the alert_id so the link never points at the generic alerts index.
  const alertUrl =
    (typeof result?.alert_url === "string" && result.alert_url) ||
    (alertId ? `https://xynth.com/alerts/${alertId}` : null);

  if (result?.status === "cancelled") {
    // Distinguish mid-upload (rollback ran) vs pre-publish cancel. Both
    // show the same calm card; mid-upload adds a tiny line confirming the
    // rollback so the user knows nothing was left half-created.
    const midFlight = result?.cancelled_mid_upload === true;
    const cancelStage = typeof result?.cancelled_at_stage === "string" ? result.cancelled_at_stage : null;
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader t={SLATE} Icon={X} title="Alert upload cancelled" />
          {midFlight && (
            <div className={`px-3 pb-2.5 text-[11px] ${SLATE.subLight}`}>
              Rolled back cleanly{cancelStage ? ` (stage: ${cancelStage})` : ""}. No alert was saved.
            </div>
          )}
        </AlertCardShell>
      </motion.div>
    );
  }

  // Preflight gate — backend wants the agent to tighten up the cells before
  // publishing. Render a neutral "tightening up..." loader that mirrors the
  // first_run_failed retry card. No technical language ever reaches the user.
  if (result?.status === "needs_review") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="w-full"
      >
        <div className="rounded-md border border-white/10 bg-white/[0.03] overflow-hidden">
          <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
            <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin shrink-0" strokeWidth={2} />
            <span className="text-[13px] font-medium text-white/85 truncate">
              Tightening up your alert
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // First-run failed — show calm "Retrying..." card. Hide the scary error / traceback
  // from the user; the agent handles retries automatically.
  if (result?.first_run_failed === true) {
    const retryAttempts = result?.retry_attempts ?? 0;
    const giveUp = retryAttempts >= 2;
    return (
      <motion.div
        initial={{ opacity: 0, y: 4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="w-full"
      >
        <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
          <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
            <Loader2 className={`w-3.5 h-3.5 ${chrome.iconMuted} animate-spin shrink-0`} strokeWidth={2} />
            <span className={`text-[13px] font-medium ${chrome.text} truncate`}>
              {giveUp ? "Needs your help" : "Adjusting alert logic"}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // First-run still executing in the cloud after the backend wait window.
  // The backend keeps watching and auto-activates on success, so show an
  // honest amber "finishing up" card, NOT the green live card.
  if (result?.first_run_pending === true) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="w-full"
      >
        <div className="rounded-md border border-amber-400/25 bg-amber-400/[0.05] overflow-hidden">
          <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
            <Loader2 className="w-3.5 h-3.5 text-amber-300 animate-spin shrink-0" strokeWidth={2} />
            <span className="text-[13px] font-medium text-white/85 truncate">
              Test run still in progress. Your alert goes live automatically when it finishes.
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  const isSuccess = result?.status === "success" || result?.alert_id;
  if (!isSuccess) {
    const errMsg = (result?.message as string) || "Alert setup failed";
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={DESTRUCTIVE}>
          <div className="flex items-start gap-3">
            <div
              className={`w-7 h-7 ${RADIUS.chip} ${DESTRUCTIVE.iconBg} flex items-center justify-center shrink-0`}
            >
              <TriangleAlert
                className={`w-3.5 h-3.5 ${DESTRUCTIVE.iconFg}`}
                strokeWidth={2}
              />
            </div>
            <span className={`text-sm ${DESTRUCTIVE.subLight} leading-snug`}>
              {errMsg.slice(0, 240)}
            </span>
          </div>
        </AlertCardShell>
      </motion.div>
    );
  }

  // ── Success: minimal one-liner — [bell] title · live dot · View ──
  return (
    <motion.div
      {...SPRING_IN}
      className="w-full"
      // Buddy-tour anchor for the post-publish "alert is live" beat.
      // After upload_alert returns, the replay emits a buddy beat that
      // spotlights this success card and points the click-target ring
      // at the View link (which goes to xynth.com/alerts/...).
      data-testid="upload-alert-success"
    >
      <div
        className={`relative ${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm`}
      >
        <motion.span
          aria-hidden="true"
          className={`absolute -inset-px ${RADIUS.card} pointer-events-none`}
          {...SUCCESS_BURST}
        />
        <div className="relative px-3 py-2 flex items-center gap-2.5 min-w-0">
          <BellPlus className={`w-3.5 h-3.5 ${t.iconFg} shrink-0`} strokeWidth={2.2} />
          <span className={`${TITLE_CLASS} truncate flex-1 min-w-0`}>{alertName}</span>
          <span
            aria-label="Alert is live"
            title="Live"
            className="shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
          />
          {alertUrl && (
            <a
              href={alertUrl}
              // Same-tab SPA navigation on purpose: the replay is a
              // continuous, guided experience — opening the live
              // alert in a new tab fragments the flow (the buddy
              // tour would be stranded behind the user). We
              // preventDefault() so the native anchor doesn't do a
              // full browser navigation (which would reload the
              // bundle + drop React state), then use
              // router.push() to trigger Next.js's client-side
              // transition to /alerts/[id]. The buddy overlay's
              // document-level click listener STILL sees the click
              // event bubble (preventDefault doesn't stop
              // propagation) so it releases the beat before we
              // navigate — no soft-lock.
              //
              // `alertUrl` is typically absolute (xynth.com/alerts/…)
              // so on localhost the origin check fails and a native
              // nav would send the dev user to prod. We always
              // compute a relative path from `alertId` so router.push
              // stays on the current origin; the anchor href still
              // points at the canonical absolute URL so
              // right-click-copy-link / share-link behaviour works.
              onClick={(e) => {
                if (!alertId) return;
                e.preventDefault();
                // Append the onboarding flag so AlertPageBuddyTour
                // fires on landing and the viewer gets a seamless
                // continuation of the replay tour onto the alert
                // page. Stripped by the tour once it completes.
                // Clean replay route opts out of the handoff so the
                // alert page loads bare (no buddy spotlight).
                const target = isCleanReplay
                  ? `/alerts/${alertId}`
                  : `/alerts/${alertId}?${ONBOARDING_QUERY_PARAM}=${ONBOARDING_QUERY_VALUE}`;
                router.push(target);
              }}
              aria-label={`View alert ${alertName}`}
              // Buddy click_target anchor — pulsing green ring +
              // "Click" pill point at this link. Clicking it
              // navigates to the live alert page AND releases the
              // buddy beat (the overlay's click listener fires on
              // the same event regardless of preventDefault).
              data-testid="upload-alert-view-link"
              className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium ${t.accentText} hover:underline transition-colors ${focusRing(viewMode)}`}
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DeleteAlertDisplay — DESTRUCTIVE confirm + success burst (NO red)
// ─────────────────────────────────────────────────────────────────────────────

export function DeleteAlertDisplay({
  status,
  result,
  args: _args,
  sessionId,
  toolCallId,
}: AlertToolDisplayProps) {
  const confirmation = useToolConfirmation(sessionId, toolCallId, status === "loading");

  if (status === "loading") {
    const preview = confirmation.preview || {};
    const alertsToDelete: any[] = Array.isArray(preview?.alerts) ? preview.alerts : [];
    const ready = Boolean(confirmation.preview) && alertsToDelete.length > 0;

    if (confirmation.decision === "done" || confirmation.decision === "confirming") {
      return <InlineLoader tone="destructive" label="Deleting…" />;
    }
    if (confirmation.decision === "cancelling") {
      return <InlineLoader tone="slate" label="Cancelling…" />;
    }

    if (!ready) {
      return <InlineLoader tone="destructive" label="Loading alerts to delete…" />;
    }

    const multi = alertsToDelete.length > 1;
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <div className={`${RADIUS.card} border ${DESTRUCTIVE.ring} ${DESTRUCTIVE.surface} overflow-hidden shadow-sm`}>
          <div className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-center">
            <div className="flex items-center gap-2 min-w-0">
              <Trash2 className={`w-4 h-4 ${DESTRUCTIVE.iconFg} shrink-0`} strokeWidth={2.2} />
              <span className={`${TITLE_CLASS} truncate`}>
                {multi ? `Delete ${alertsToDelete.length} alerts?` : `Delete ${alertsToDelete[0]?.name || "alert"}?`}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                aria-label="Cancel"
                title="Cancel"
                onClick={() => confirmation.submit("cancel")}
                className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 text-slate-100 transition-colors ${focusRing()}`}
              >
                <X className="w-4 h-4" strokeWidth={2.4} />
              </button>
              <button
                type="button"
                aria-label={multi ? "Delete all" : "Delete"}
                title={multi ? "Delete all" : "Delete"}
                onClick={() => confirmation.submit("confirm")}
                className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} bg-slate-100 hover:bg-white text-slate-950 transition-colors ${focusRing()}`}
              >
                <Check className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>
          </div>
          {confirmation.error && (
            <div className={`border-t ${DESTRUCTIVE.ring} px-3 py-2 text-xs ${DESTRUCTIVE.accentText}`}>
              {confirmation.error}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // ── Result states ──────────────────────────────────────────────────
  if (result?.status === "cancelled") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={X}
            title="Delete cancelled — nothing was removed"
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  const deletedCount = result?.deleted_count || (result?.status === "success" ? 1 : 0);
  const deletedNames = result?.deleted_names || [];
  const deletedNotebooks = result?.deleted_notebooks || [];
  const isSuccess = result?.status === "success" || deletedCount > 0;
  const shouldRedirectHome = Boolean(result?.redirect_to_home);
  const displayText =
    deletedCount === 1
      ? deletedNames[0] || result?.deleted_alert?.name || "1 alert"
      : `${deletedCount} alerts`;

  // If the agent just deleted the alert that OWNS this chat session (i.e. the
  // linked-alert session backing /alerts/{id}), the backend marks the tool
  // result with redirect_to_home=true. The session has nowhere to go after
  // that — the linked alert is gone — so we hard-redirect to xynth.com after
  // a short delay so the user sees the success card first.
  useEffect(() => {
    if (!isSuccess || !shouldRedirectHome) return;
    console.info(
      "[delete-alert-display] linked alert deleted → redirecting to xynth.com in 1.2s",
      { deletedNames, deletedNotebooks, sessionId },
    );
    const timer = window.setTimeout(() => {
      window.location.href = "https://xynth.com";
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [isSuccess, shouldRedirectHome, deletedNames, deletedNotebooks, sessionId]);

  if (!isSuccess) {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={DESTRUCTIVE}>
          <InlineHeader t={DESTRUCTIVE} Icon={TriangleAlert} title="Delete failed" />
        </AlertCardShell>
      </motion.div>
    );
  }

  const hasNotebooks = deletedNotebooks.length > 0;
  const subCopy = shouldRedirectHome
    ? "Closing this chat…"
    : hasNotebooks
    ? `Alert${deletedNotebooks.length > 1 ? "s" : ""} and schedule removed.`
    : "Alert and schedule removed.";

  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div
        className={`relative ${RADIUS.card} border ${DESTRUCTIVE.ring} ${DESTRUCTIVE.surface} overflow-hidden shadow-sm`}
      >
        <motion.span
          aria-hidden="true"
          className={`absolute -inset-px ${RADIUS.card} pointer-events-none`}
          {...SUCCESS_BURST}
        />
        <div className={`relative ${PAD.default} flex items-center gap-3`}>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 380, damping: 18 }}
            className={`w-7 h-7 ${RADIUS.chip} ${DESTRUCTIVE.iconBg} flex items-center justify-center shrink-0`}
          >
            <Trash2 className={`w-3.5 h-3.5 ${DESTRUCTIVE.iconFg}`} strokeWidth={2.2} />
          </motion.div>
          <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            <span className={`${TITLE_CLASS} truncate`}>Removed {displayText}</span>
            <span className={SUB_CLASS}>{subCopy}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EditAlertDisplay — handoff card that opens /alerts/{id}?q=<query> in a new
// tab. Unlike upload/delete cards this is NOT a Redis-gated confirmation —
// opening a browser tab is a client-only action, so we simply render the card
// and let the user click "Open editor". No backend roundtrip on click.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Local state of the card after render. We persist across remounts via
 * sessionStorage so if the message list unmounts/remounts (e.g. after a
 * route change) the user doesn't see the "open editor" button come back
 * after they've already dismissed or used it.
 */
type EditAlertLocalState = "idle" | "opened" | "dismissed";

function readEditLocalState(toolCallId: string): EditAlertLocalState {
  if (typeof window === "undefined" || !toolCallId) return "idle";
  try {
    const raw = window.sessionStorage.getItem(`edit_alert:${toolCallId}`);
    if (raw === "opened" || raw === "dismissed") return raw;
  } catch {
    // noop
  }
  return "idle";
}

function writeEditLocalState(toolCallId: string, state: EditAlertLocalState) {
  if (typeof window === "undefined" || !toolCallId) return;
  try {
    window.sessionStorage.setItem(`edit_alert:${toolCallId}`, state);
  } catch {
    // noop
  }
}

/**
 * Extract just the hostname from an edit_url for logging. We avoid logging
 * the full URL when it contains the raw query so we don't drown logs in
 * long strings; the full URL is logged separately at open time. Returns
 * the original string if URL parsing fails (logs "parse_failed:<first 40>").
 */
function safeUrlHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return `parse_failed:${url.slice(0, 40)}`;
  }
}

export function EditAlertDisplay({
  status,
  result,
  args,
  toolCallId,
  viewMode,
}: AlertToolDisplayProps) {
  const t = alertTone(viewMode);
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const loaderTone: AlertTone = viewMode === "signal" ? "emerald" : "icy";
  const callId = toolCallId || "";
  const [localState, setLocalState] = useState<EditAlertLocalState>(() =>
    readEditLocalState(callId),
  );

  if (status === "loading") {
    return <InlineLoader tone={loaderTone} label="Preparing edit…" />;
  }

  // Backend errored (not owner, alert deleted, bad id, etc.) — simple slate card
  if (result?.status === "error") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={TriangleAlert}
            title="Couldn't prepare the edit"
          />
          {result?.message && (
            <span className={`${BODY_CLASS} text-slate-400`}>
              {String(result.message).slice(0, 200)}
            </span>
          )}
        </AlertCardShell>
      </motion.div>
    );
  }

  // Happy path — pull fields, fall back to args where backend omitted them
  const alertId: string = result?.alert_id || args?.alert_id || "";
  const alertName: string =
    result?.alert_name || "Alert";
  const schedule: string =
    result?.schedule_description || "";
  const editQuery: string =
    result?.edit_query || args?.query || "";
  // Backend builds the canonical URL (URL-encoded); we use it verbatim.
  // Fallback constructed client-side only if the backend omitted it
  // (shouldn't happen, but defensive).
  const editUrl: string =
    result?.edit_url ||
    (alertId && editQuery
      ? `https://xynth.com/alerts/${alertId}?q=${encodeURIComponent(editQuery)}`
      : "");

  // Log what the card actually has to work with. If editUrl is empty or
  // alert_id/query are missing, the "Open editor" button stays disabled
  // and the user sees a card but nothing happens on click.
  handoffLog("editcard:result", {
    status: result?.status || null,
    hasEditUrl: Boolean(editUrl),
    editUrlHost: editUrl ? safeUrlHost(editUrl) : null,
    editUrlHasQ: editUrl ? editUrl.includes("?q=") : false,
    hasAlertId: Boolean(alertId),
    hasEditQuery: Boolean(editQuery),
    editQueryLen: editQuery?.length ?? 0,
    alertId: alertId || null,
    toolCallId: callId || null,
    usedFallbackUrl: !result?.edit_url && Boolean(editUrl),
  });

  const handleOpen = () => {
    if (!editUrl) {
      handoffLog("editcard:opening:aborted", {
        reason: "no_edit_url",
        toolCallId: callId || null,
      });
      telemetry.emit("alert.edit_card_blocked", {
        session_id: String(result?.id || result?.session_id || args?.session_id || alertId || ""),
        alert_id: alertId || null,
        reason: "no_edit_url",
        tool_call_id: callId || null,
      });
      return;
    }
    handoffLog("editcard:opening", {
      editUrl,
      editUrlHost: safeUrlHost(editUrl),
      editUrlHasQ: editUrl.includes("?q="),
      alertId: alertId || null,
      queryLen: editQuery?.length ?? 0,
      toolCallId: callId || null,
    });
    telemetry.emit("alert.edit_card_open", {
      session_id: String(result?.id || result?.session_id || args?.session_id || alertId || ""),
      alert_id: alertId || null,
      tool_call_id: callId || null,
      edit_url_host: safeUrlHost(editUrl),
      edit_url_has_query: editUrl.includes("?"),
    });
    // `noopener,noreferrer` prevents the opened tab from accessing
    // `window.opener` on the parent (standard security hygiene when opening
    // user-triggered external tabs).
    window.open(editUrl, "_blank", "noopener,noreferrer");
    setLocalState("opened");
    writeEditLocalState(callId, "opened");
  };

  const handleDismiss = () => {
    setLocalState("dismissed");
    writeEditLocalState(callId, "dismissed");
  };

  // ── After the user has acted, show a compact "done" state ──
  if (localState === "opened") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={t}>
          <InlineHeader
            t={t}
            Icon={ExternalLink}
            title={`Opened editor for ${alertName}`}
            right={
              <span className={`${SUB_CLASS} truncate max-w-[200px]`}>
                New tab
              </span>
            }
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  if (localState === "dismissed") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={X}
            title={`Dismissed edit for ${alertName}`}
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  // ── Fresh handoff card ──
  // Minimal: mirrors the Publish/Delete confirm pattern — single row header
  // with icon-only X / open buttons, and (optional) a compact query line
  // beneath it. No schedule, no "YOUR REQUEST" label box, no helper sentence.
  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div
        className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm`}
      >
        <div className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-center">
          <div className="flex items-center gap-2 min-w-0">
            <Pencil
              className={`w-4 h-4 ${t.iconFg} shrink-0`}
              strokeWidth={2.2}
            />
            <span className={`${TITLE_CLASS} truncate`}>
              {`Edit "${alertName}"`}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              aria-label="Not now"
              title="Not now"
              onClick={handleDismiss}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 text-slate-100 transition-colors ${focusRing(viewMode)}`}
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              aria-label="Open editor"
              title="Open editor"
              onClick={handleOpen}
              disabled={!editUrl}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} ${chrome.button} disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${focusRing(viewMode)}`}
            >
              <Check className="w-4 h-4" strokeWidth={2.4} />
            </button>
          </div>
        </div>
        {editQuery && (
          <div className={`border-t ${t.ring} px-3 py-2`}>
            <p className={`text-xs ${t.subLight} italic leading-snug truncate`}>
              “{editQuery}”
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ForkAlertDisplay — visitor handoff card that opens /?q=<query> in main chat.
// This mirrors EditAlertDisplay but labels the action as "fork", not "edit".
// ─────────────────────────────────────────────────────────────────────────────

type ForkAlertLocalState = "idle" | "opened" | "dismissed";

function readForkLocalState(toolCallId: string): ForkAlertLocalState {
  if (typeof window === "undefined" || !toolCallId) return "idle";
  try {
    const raw = window.sessionStorage.getItem(`fork_alert:${toolCallId}`);
    if (raw === "opened" || raw === "dismissed") return raw;
  } catch {
    // noop
  }
  return "idle";
}

function writeForkLocalState(toolCallId: string, state: ForkAlertLocalState) {
  if (typeof window === "undefined" || !toolCallId) return;
  try {
    window.sessionStorage.setItem(`fork_alert:${toolCallId}`, state);
  } catch {
    // noop
  }
}

export function ForkAlertDisplay({
  status,
  result,
  args,
  sessionId,
  toolCallId,
  viewMode,
}: AlertToolDisplayProps) {
  const t = alertTone(viewMode);
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const loaderTone: AlertTone = viewMode === "signal" ? "emerald" : "icy";
  const callId = toolCallId || "";
  const [localState, setLocalState] = useState<ForkAlertLocalState>(() =>
    readForkLocalState(callId),
  );

  if (status === "loading") {
    return <InlineLoader tone={loaderTone} label="Preparing fork…" />;
  }

  if (result?.status === "error") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={TriangleAlert}
            title="Couldn't prepare the fork"
          />
          {result?.message && (
            <span className={`${BODY_CLASS} text-slate-400`}>
              {String(result.message).slice(0, 200)}
            </span>
          )}
        </AlertCardShell>
      </motion.div>
    );
  }

  const alertId: string = result?.alert_id || args?.alert_id || "";
  const alertName: string = result?.alert_name || "Alert";
  const forkQuery: string = result?.fork_query || args?.query || "";
  const forkUrl: string =
    result?.fork_url ||
    (forkQuery ? `https://xynth.com/?q=${encodeURIComponent(forkQuery)}&model_tier=max` : "");

  handoffLog("forkcard:result", {
    status: result?.status || null,
    hasForkUrl: Boolean(forkUrl),
    forkUrlHost: forkUrl ? safeUrlHost(forkUrl) : null,
    forkUrlHasQ: forkUrl ? forkUrl.includes("?q=") : false,
    alertId: alertId || null,
    toolCallId: callId || null,
    usedFallbackUrl: !result?.fork_url && Boolean(forkUrl),
  });

  const handleOpen = () => {
    if (!forkUrl) {
      handoffLog("forkcard:opening:aborted", {
        reason: "no_fork_url",
        toolCallId: callId || null,
      });
      telemetry.emit("alert.fork_card_blocked", {
        session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
        alert_id: alertId || null,
        reason: "no_fork_url",
        tool_call_id: callId || null,
      });
      return;
    }
    handoffLog("forkcard:opening", {
      forkUrl,
      forkUrlHost: safeUrlHost(forkUrl),
      forkUrlHasQ: forkUrl.includes("?q="),
      alertId: alertId || null,
      queryLen: forkQuery?.length ?? 0,
      toolCallId: callId || null,
    });
    telemetry.emit("alert.fork_card_open", {
      session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
      alert_id: alertId || null,
      alert_name: alertName,
      tool_call_id: callId || null,
      fork_url_host: safeUrlHost(forkUrl),
      fork_url_has_q: forkUrl.includes("?q="),
    });
    window.open(forkUrl, "_blank", "noopener,noreferrer");
    setLocalState("opened");
    writeForkLocalState(callId, "opened");
  };

  const handleDismiss = () => {
    telemetry.emit("alert.fork_card_dismiss", {
      session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
      alert_id: alertId || null,
      alert_name: alertName,
      tool_call_id: callId || null,
    });
    setLocalState("dismissed");
    writeForkLocalState(callId, "dismissed");
  };

  if (localState === "opened") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={t}>
          <InlineHeader
            t={t}
            Icon={ExternalLink}
            title={`Opened chat for ${alertName}`}
            right={
              <span className={`${SUB_CLASS} truncate max-w-[200px]`}>
                New tab
              </span>
            }
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  if (localState === "dismissed") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={X}
            title={`Dismissed fork for ${alertName}`}
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div
        className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm`}
      >
        <div className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-center">
          <div className="flex items-center gap-2 min-w-0">
            <GitFork
              className={`w-4 h-4 ${t.iconFg} shrink-0`}
              strokeWidth={2.2}
            />
            <span className={`${TITLE_CLASS} truncate`}>
              {`Fork "${alertName}"`}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              aria-label="Not now"
              title="Not now"
              onClick={handleDismiss}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 text-slate-100 transition-colors ${focusRing(viewMode)}`}
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              aria-label="Open chat"
              title="Open chat"
              onClick={handleOpen}
              disabled={!forkUrl}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} ${chrome.button} disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${focusRing(viewMode)}`}
            >
              <Check className="w-4 h-4" strokeWidth={2.4} />
            </button>
          </div>
        </div>
        {forkQuery && (
          <div className={`border-t ${t.ring} px-3 py-2`}>
            <p className={`text-xs ${t.subLight} italic leading-snug truncate`}>
              “{forkQuery}”
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateAlertDisplay — visitor handoff card that opens /?q=<query> in main chat.
// Unlike fork_alert, this starts a brand-new alert idea from scratch.
// ─────────────────────────────────────────────────────────────────────────────

type CreateAlertLocalState = "idle" | "opened" | "dismissed";

function readCreateLocalState(toolCallId: string): CreateAlertLocalState {
  if (typeof window === "undefined" || !toolCallId) return "idle";
  try {
    const raw = window.sessionStorage.getItem(`create_alert:${toolCallId}`);
    if (raw === "opened" || raw === "dismissed") return raw;
  } catch {
    // noop
  }
  return "idle";
}

function writeCreateLocalState(toolCallId: string, state: CreateAlertLocalState) {
  if (typeof window === "undefined" || !toolCallId) return;
  try {
    window.sessionStorage.setItem(`create_alert:${toolCallId}`, state);
  } catch {
    // noop
  }
}

export function CreateAlertDisplay({
  status,
  result,
  args,
  sessionId,
  toolCallId,
  viewMode,
}: AlertToolDisplayProps) {
  const t = alertTone(viewMode);
  const chrome = getViewModeChrome(viewMode ?? "alert");
  const loaderTone: AlertTone = viewMode === "signal" ? "emerald" : "icy";
  const callId = toolCallId || "";
  const [localState, setLocalState] = useState<CreateAlertLocalState>(() =>
    readCreateLocalState(callId),
  );

  if (status === "loading") {
    return <InlineLoader tone={loaderTone} label="Preparing alert setup…" />;
  }

  if (result?.status === "error") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={TriangleAlert}
            title="Couldn't prepare the alert setup"
          />
          {result?.message && (
            <span className={`${BODY_CLASS} text-slate-400`}>
              {String(result.message).slice(0, 200)}
            </span>
          )}
        </AlertCardShell>
      </motion.div>
    );
  }

  const alertId: string = result?.alert_id || args?.alert_id || "";
  const alertName: string = result?.alert_name || "this page";
  const createQuery: string = result?.create_query || args?.query || "";
  const createUrl: string =
    result?.create_url ||
    (createQuery ? `https://xynth.com/?q=${encodeURIComponent(createQuery)}&model_tier=max` : "");

  handoffLog("createcard:result", {
    status: result?.status || null,
    hasCreateUrl: Boolean(createUrl),
    createUrlHost: createUrl ? safeUrlHost(createUrl) : null,
    createUrlHasQ: createUrl ? createUrl.includes("?q=") : false,
    alertId: alertId || null,
    toolCallId: callId || null,
    usedFallbackUrl: !result?.create_url && Boolean(createUrl),
  });

  const handleOpen = () => {
    if (!createUrl) {
      handoffLog("createcard:opening:aborted", {
        reason: "no_create_url",
        toolCallId: callId || null,
      });
      telemetry.emit("alert.create_card_blocked", {
        session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
        alert_id: alertId || null,
        reason: "no_create_url",
        tool_call_id: callId || null,
      });
      return;
    }
    handoffLog("createcard:opening", {
      createUrl,
      createUrlHost: safeUrlHost(createUrl),
      createUrlHasQ: createUrl.includes("?q="),
      alertId: alertId || null,
      queryLen: createQuery?.length ?? 0,
      toolCallId: callId || null,
    });
    telemetry.emit("alert.create_card_open", {
      session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
      alert_id: alertId || null,
      alert_name: alertName,
      tool_call_id: callId || null,
      create_url_host: safeUrlHost(createUrl),
      create_url_has_q: createUrl.includes("?q="),
    });
    window.open(createUrl, "_blank", "noopener,noreferrer");
    setLocalState("opened");
    writeCreateLocalState(callId, "opened");
  };

  const handleDismiss = () => {
    telemetry.emit("alert.create_card_dismiss", {
      session_id: String(sessionId || result?.id || result?.session_id || args?.session_id || ""),
      alert_id: alertId || null,
      alert_name: alertName,
      tool_call_id: callId || null,
    });
    setLocalState("dismissed");
    writeCreateLocalState(callId, "dismissed");
  };

  if (localState === "opened") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={t}>
          <InlineHeader
            t={t}
            Icon={ExternalLink}
            title="Opened new alert chat"
            right={
              <span className={`${SUB_CLASS} truncate max-w-[200px]`}>
                New tab
              </span>
            }
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  if (localState === "dismissed") {
    return (
      <motion.div {...SPRING_IN} className="w-full">
        <AlertCardShell t={SLATE}>
          <InlineHeader
            t={SLATE}
            Icon={X}
            title="Dismissed alert setup"
          />
        </AlertCardShell>
      </motion.div>
    );
  }

  return (
    <motion.div {...SPRING_IN} className="w-full">
      <div
        className={`${RADIUS.card} border ${t.ring} ${t.surface} overflow-hidden shadow-sm`}
      >
        <div className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 items-center">
          <div className="flex items-center gap-2 min-w-0">
            <BellPlus
              className={`w-4 h-4 ${t.iconFg} shrink-0`}
              strokeWidth={2.2}
            />
            <span className={`${TITLE_CLASS} truncate`}>
              Create your own alert
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              aria-label="Not now"
              title="Not now"
              onClick={handleDismiss}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} border border-slate-400/30 bg-transparent hover:bg-slate-100/5 hover:border-slate-300/50 text-slate-100 transition-colors ${focusRing(viewMode)}`}
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <button
              type="button"
              aria-label="Open chat"
              title="Open chat"
              onClick={handleOpen}
              disabled={!createUrl}
              className={`inline-flex items-center justify-center w-8 h-8 ${RADIUS.button} ${chrome.button} disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${focusRing(viewMode)}`}
            >
              <Check className="w-4 h-4" strokeWidth={2.4} />
            </button>
          </div>
        </div>
        {createQuery && (
          <div className={`border-t ${t.ring} px-3 py-2`}>
            <p className={`text-xs ${t.subLight} italic leading-snug truncate`}>
              “{createQuery}”
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
